import { FreshnessNote } from "@/components/FreshnessNote";
import { freshnessDate } from "@/lib/currentYear";
import { useState, useMemo, useCallback, lazy, Suspense } from "react";
import { getSiteOrigin } from "@/lib/ssr-safe";
import { useNavigate } from "@/lib/router-compat";
import { Database } from "@/integrations/supabase/types";

// Lazy load card components
const CardCard = lazy(() => import("@/components/CardCard").then(m => ({ default: m.CardCard })));
const CardTableRow = lazy(() => import("@/components/CardTableRow").then(m => ({ default: m.CardTableRow })));
const CompareDialog = lazy(() => import("@/components/CompareDialog").then(m => ({ default: m.CompareDialog })));
const CompareDialogDE = lazy(() => import("@/components/CompareDialogDE").then(m => ({ default: m.CompareDialogDE })));

import { CompareBar } from "@/components/CompareBar";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ModernFilter } from "@/components/ModernFilter";
import { SectionRenderer } from "@/components/SectionRenderer";
import { SegmentLinks } from "@/components/SegmentLinks";
import { SEOHelmet } from "@/components/SEOHelmet";
import { useAIAssistant } from "@/contexts/AIAssistantContext";
import { useCards } from "@/hooks/useCards";
import { useCardOverrides } from "@/hooks/useCardOverrides";
import { useOffers } from "@/hooks/useOffers";
import { useAdPlacements } from "@/hooks/useAdPlacements";
import { useFeedSettings } from "@/hooks/useFeedSettings";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useCardListingSettings } from "@/hooks/useCardListingSettings";
import { useComparison } from "@/hooks/useComparison";
import { adaptSupabaseCardToCompareCard } from "@/lib/cardAdapter";
import { adaptSupabaseCardToGermanCompareCard } from "@/lib/cardAdapterDE-Comparison";
import { normalizeDbCard, toListCardVM, toTableRowVM } from "@/lib/cardCoreNormalizer";
import { CreditCard } from "@/types/card";
import { GermanCompareCard } from "@/types/cardDE";
import { searchResultsJsonLd } from "@/lib/schema-cards";
import { generateSearchActionSchema, generateOrganizationSchema, generateSiteNavigationSchema } from "@/lib/schemaGenerator";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CardSkeleton } from "@/components/CardSkeleton";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type Card = Database['public']['Tables']['cards']['Row'];

export default function CardListing() {
  const navigate = useNavigate();
  const { data: cards = [], isLoading: cardsLoading } = useCards();
  const { data: overrides = [] } = useCardOverrides();
  const { data: offers = [] } = useOffers();
  const { data: adPlacements = [] } = useAdPlacements();
  const { data: feedSettings } = useFeedSettings();
  const { data: siteSettings } = useSiteSettings();
  const { data: listingSettings } = useCardListingSettings();
  const { openAIDialog } = useAIAssistant();
  
  const {
    selectedCards,
    addCard,
    removeCard,
    clearAll,
  } = useComparison();

  const domain = getSiteOrigin();

  // View state - default to list view, or use setting from backend
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [showCompareDialog, setShowCompareDialog] = useState(false);

  // Filter state - neutral values to show all cards by default
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState("recommended");
  const [age, setAge] = useState(18);
  const [income, setIncome] = useState(0);
  const [creditFrame, setCreditFrame] = useState(0);
  const [effectiveRate, setEffectiveRate] = useState(999);

  // Process cards
  const sponsoredCardIds = useMemo(() => 
    new Set(adPlacements.map(ad => ad.card_id)), 
    [adPlacements]
  );
  
   const sponsoredLabels = useMemo(() => {
     const map = new Map();
     adPlacements.forEach(ad => map.set(ad.card_id, ad.label || 'Anzeige'));
     return map;
   }, [adPlacements]);
  
  const offersMap = useMemo(() => {
    const map = new Map();
    offers.forEach(offer => {
      if (offer.card_id) {
        map.set(offer.card_id, offer);
      }
    });
    return map;
  }, [offers]);

  const processedCards = useMemo(() => {
    return cards.map(card => {
      const normalized = normalizeDbCard(card);
      return {
        ...card,
        ...normalized,
        id: String(card.id), // Ensure string type
      };
    });
  }, [cards, overrides]);

  // Filter and sort
  const filteredAndSortedCards = useMemo(() => {
    let filtered = [...processedCards];

    // Search filter
    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (card) =>
          card.name.toLowerCase().includes(query) ||
          card.issuer.toLowerCase().includes(query) ||
          card.description?.toLowerCase().includes(query)
      );
    }

    // Goal-based filters
    if (selectedFilters.length > 0) {
      filtered = filtered.filter((card) => {
        return selectedFilters.every((filter) => {
          switch (filter) {
            case "no-fee":
              return card.annual_fee === 0;
            case "travel":
              return card.travel_insurance === true;
            case "apple-pay":
              return card.apple_pay === true;
            case "google-pay":
              return card.google_pay === true;
            case "low-rate":
              return (card.apr_effective || 999) < 22;
            case "cashback":
              const benefitsStr = card.benefits ? String(card.benefits) : '';
              const perksStr = card.perks ? String(card.perks) : '';
              const searchText = `${benefitsStr} ${perksStr}`.toLowerCase();
              return searchText.includes('cashback') || searchText.includes('pengene tilbake');
            case "prepaid":
              const cardAny = card as any;
              return !!cardAny.is_prepaid;
            case "with-account":
              return !!(card as any).current_account_included;
            case "discounts":
              return !!(card as any).display_config?.hasDiscounts;
            case "hotline":
              return !!(card as any).hotline_available;
            default:
              return true;
          }
        });
      });
    }

    // Personal filters - only filter if values are not at neutral settings
    if (age > 18) {
      filtered = filtered.filter(card => (card.age_min || 18) <= age);
    }
    if (income > 0) {
      filtered = filtered.filter(card => !card.income_min || card.income_min <= income);
    }
    if (creditFrame > 0) {
      filtered = filtered.filter(card => {
        const minLimit = card.credit_limit_min || 0;
        const maxLimit = card.credit_limit_max || Infinity;
        return creditFrame >= minLimit && creditFrame <= maxLimit;
      });
    }
    if (effectiveRate < 999) {
      filtered = filtered.filter(card => !card.apr_effective || card.apr_effective <= effectiveRate);
    }

    // Sort
    filtered.sort((a, b) => {
      // Promoted cards always first
      const aSponsored = sponsoredCardIds.has(a.id);
      const bSponsored = sponsoredCardIds.has(b.id);
      if (aSponsored && !bSponsored) return -1;
      if (!aSponsored && bSponsored) return 1;

      // Then by selected sort
      switch (sortBy) {
        case "low-apr":
          return (a.apr_effective || 999) - (b.apr_effective || 999);
        case "no-fee":
          return (a.annual_fee || 999999) - (b.annual_fee || 999999);
        case "newest":
          return (b.boost_priority || 0) - (a.boost_priority || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [processedCards, searchTerm, selectedFilters, sortBy, age, income, creditFrame, effectiveRate, sponsoredCardIds]);

  // Generate view models
  const gridViewCards = useMemo(() => {
    return filteredAndSortedCards.map(card => ({
      card,
      tile: toListCardVM(card, offersMap.get(card.id))
    }));
  }, [filteredAndSortedCards, offersMap]);

  const tableViewCards = useMemo(() => {
    return filteredAndSortedCards.map(card => ({
      card,
      row: toTableRowVM(card, offersMap.get(card.id))
    }));
  }, [filteredAndSortedCards, offersMap]);

  // Generate schemas
  const cardsSchemas = useMemo(() => {
    if (!filteredAndSortedCards.length) return [];

    const items = filteredAndSortedCards.map(card => ({
      core: card,
      url: `${domain}/kreditkarten/${card.slug}`,
      image: card.logo_url
    }));

    return searchResultsJsonLd({
      items: items as Parameters<typeof searchResultsJsonLd>[0]["items"],
      pageUrl: `${domain}/kreditkarten`,
      breadcrumb: [
        { name: "Startseite", url: domain },
        { name: "Kreditkarten", url: `${domain}/kreditkarten` },
      ],
    });
  }, [filteredAndSortedCards, domain]);

  const organizationSchema = generateOrganizationSchema(
    domain,
    siteSettings?.site_name || "Kredittkort.com",
    siteSettings?.organization_name ?? "",
    siteSettings?.site_logo_url
  );
  
  const searchActionSchema = generateSearchActionSchema(domain);
  const siteNavigationSchema = generateSiteNavigationSchema(domain);

  // Handlers
  const handleNavigate = useCallback((page: string) => {
    if (page === 'home') {
      navigate('/');
    }
  }, [navigate]);

  const handleViewDetails = useCallback((slug: string) => {
    navigate(`/kreditkarten/${slug}`);
  }, [navigate]);

  const handleViewAllCards = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleFilterToggle = useCallback((filter: string) => {
    setSelectedFilters(prev => 
      prev.includes(filter) 
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  }, []);

  const handleResetAll = useCallback(() => {
    setSearchTerm("");
    setSelectedFilters([]);
    setSortBy("recommended");
    setAge(18);
    setIncome(0);
    setCreditFrame(0);
    setEffectiveRate(999);
  }, []);

  const handleToggleCompare = useCallback((cardId: string) => {
    const card = cards.find(c => String(c.id) === cardId);
    if (!card) return;
    
    const cardIdStr = String(cardId);
    if (selectedCards.some(c => c.id === cardIdStr)) {
      removeCard(cardIdStr);
    } else {
      const compareCard = adaptSupabaseCardToCompareCard(card, sponsoredCardIds.has(card.id));
      addCard(compareCard);
    }
  }, [cards, selectedCards, addCard, removeCard, sponsoredCardIds]);

  const assistantHeroTitle = feedSettings?.assistant_hero_title || "Kartenassistent";
  const assistantHeroSubtitle = feedSettings?.assistant_hero_subtitle || "Über 10.000 Nutzern geholfen";
  const assistantHeroDescription = feedSettings?.assistant_hero_description || "Beantworten Sie ein paar einfache Fragen und ich finde die perfekte Kreditkarte für Sie.";
  const assistantHeroButtonText = feedSettings?.assistant_hero_button_text || "Meine Karte finden";

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
      <SEOHelmet
        title="Alle Kreditkarten in Deutschland - Vergleichen Sie Zinsen & Gebühren"
        description="Vergleichen Sie alle Kreditkarten in Deutschland. Finden Sie die beste Karte mit niedrigen Zinsen, ohne Jahresgebühr oder mit Bonusprogramm."
        url={`${domain}/kreditkarten`}
        type="website"
        modifiedTime={freshnessDate()}
        additionalSchemas={[
          organizationSchema,
          searchActionSchema,
          ...cardsSchemas,
          siteNavigationSchema,
        ].filter(Boolean)}
      />

      <Header 
        onNavigate={handleNavigate} 
        currentPage="card-listing" 
      />

      <main className="flex-1">
        {/* Cards Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Alle Kreditkarten in Deutschland
            </h1>
            <p className="text-gray-600">
              Vergleichen Sie {cards.length} Kreditkarten und finden Sie die beste für Ihre Bedürfnisse
            </p>
            <FreshnessNote className="mt-2" label="Konditionen geprüft" />
          </div>

          <ModernFilter
            searchQuery={searchTerm}
            onSearchChange={setSearchTerm}
            selectedFilters={selectedFilters}
            onFilterToggle={handleFilterToggle}
            sortBy={sortBy}
            onSortChange={setSortBy}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            advancedFilters={{
              cardTypes: [],
              interestMax: null,
              foreignMax: null,
              issuers: [],
              nfc: false,
            }}
            onAdvancedFiltersChange={() => {}}
            availableIssuers={[]}
            chipCounts={{}}
            cardTypeCoverage={{
              credit: false,
              charge: false,
              debit: false,
            }}
            visibleCount={filteredAndSortedCards.length}
            totalCount={cards.length}
            onResetAll={handleResetAll}
          />

          {/* Cards Display */}
          <div className="mt-8">
            {cardsLoading ? (
              <div
                className={
                  viewMode === "grid"
                    ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                    : "space-y-4"
                }
              >
                {Array.from({ length: 12 }).map((_, i) => (
                  <CardSkeleton key={i} />
                ))}
              </div>
            ) : filteredAndSortedCards.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">
                  Keine Karten entsprechen Ihren Filtern
                </p>
                <Button onClick={handleResetAll} className="mt-4">
                  Filter zurücksetzen
                </Button>
              </div>
              ) : viewMode === "grid" ? (
              <Suspense fallback={
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Array.from({ length: 12 }).map((_, i) => <CardSkeleton key={i} />)}
                </div>
              }>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {gridViewCards.map(({ card, tile }, index) => (
                    <CardCard
                      key={card.id}
                      tile={tile}
                      rawCard={card}
                      offer={offersMap.get(card.id)}
                      isSelected={selectedCards.some(c => c.id === String(card.id))}
                      onToggleCompare={() => handleToggleCompare(String(card.id))}
                      compareDisabled={selectedCards.length >= 3 && !selectedCards.some(c => c.id === String(card.id))}
                      isSponsored={sponsoredCardIds.has(card.id)}
                      sponsoredLabel={sponsoredLabels.get(card.id)}
                      priority={index < 6}
                    />
                  ))}
                </div>
              </Suspense>
            ) : (
              <Suspense fallback={
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden p-8">
                  <div className="space-y-4">
                    {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
                  </div>
                </div>
              }>
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12"></TableHead>
                          <TableHead>Karte</TableHead>
                          <TableHead>Teilzahlung</TableHead>
                          <TableHead>Jahresgebühr</TableHead>
                          <TableHead>Fremdwährung</TableHead>
                          <TableHead>Bargeld</TableHead>
                          <TableHead>Vorteile</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tableViewCards.map(({ card, row }) => (
                          <CardTableRow
                            key={card.id}
                            row={row}
                            rawCard={card}
                            offer={offersMap.get(card.id)}
                            creditFrame={creditFrame}
                            isSelected={selectedCards.some(c => c.id === String(card.id))}
                            onToggleCompare={() => handleToggleCompare(String(card.id))}
                            compareDisabled={selectedCards.length >= 3 && !selectedCards.some(c => c.id === String(card.id))}
                            isSponsored={sponsoredCardIds.has(card.id)}
                            sponsoredLabel={sponsoredLabels.get(card.id)}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </Suspense>
            )}
          </div>
        </section>

        <SegmentLinks />

        {/* Sections (Popular Cards & Articles) */}
        <SectionRenderer
          currentPage="card-listing"
          onViewDetails={handleViewDetails}
          onViewAllCards={handleViewAllCards}
        />
      </main>

      <Footer onNavigate={handleNavigate} />

      {/* Comparison Bar */}
      {selectedCards.length > 0 && (
        <CompareBar
          selectedCards={selectedCards as any}
          onRemoveCard={removeCard}
          onCompare={() => setShowCompareDialog(true)}
          onClear={clearAll}
        />
      )}

      {/* Comparison Dialog */}
      <Suspense fallback={null}>
        {showCompareDialog && feedSettings?.feed_type === 'german' && (
          <CompareDialogDE
            cards={selectedCards as GermanCompareCard[]}
            open={showCompareDialog}
            onClose={() => setShowCompareDialog(false)}
          />
        )}
        {showCompareDialog && feedSettings?.feed_type !== 'german' && (
          <CompareDialog
            cards={selectedCards as CreditCard[]}
            open={showCompareDialog}
            onClose={() => setShowCompareDialog(false)}
            creditFrame={creditFrame}
          />
        )}
      </Suspense>
    </div>
  );
}
